"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import EnhancedUserAccount from "@/components/common/EnhancedUserAccount";
import { Button } from "@/components/ui/button";
import type { DynamicMessage } from "../../hooks/useLuckyWheelDynamics";

interface DynamicsScrollProps {
	messages: DynamicMessage[];
	isLoading: boolean;
	language: "zh" | "en";
}

export default function DynamicsScroll({
	messages,
	isLoading,
	language,
}: DynamicsScrollProps) {
	const [isPaused, setIsPaused] = useState(false);
	const router = useRouter();

	const translations = {
		zh: {
			loading: "加载中...",
			noData: "炉火静寂，暂无动态",
			goToForge: "前往符炉",
		},
		en: {
			loading: "Loading...",
			noData: "Forge silent, no dynamics",
			goToForge: "Go to Forge",
		},
	};

	const t = translations[language];

	// 格式化时间戳为 hh:mm
	const formatTime = (timestamp: bigint): string => {
		const ms = Number(timestamp) / 1_000_000;
		const date = new Date(ms);
		return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
	};

	// 获取消息图标
	const getMessageIcon = (type: string): string => {
		switch (type) {
			case "draw":
				return "🎰";
			case "verify":
				return "💎";
			case "commission":
				return "💰";
			default:
				return "✨";
		}
	};

	// 判断是否为高价值消息
	const isHighValueMessage = (message: DynamicMessage): boolean => {
		if (message.type === "draw") {
			const prizeName = message.extra?.prizeName || "";
			return prizeName.includes("特等奖") || prizeName.includes("一等奖") || prizeName.includes("奥丁圣印");
		}
		if (message.type === "verify") {
			const prizeLevel = Number(message.extra?.prizeLevel || 0);
			return prizeLevel >= 81; // 传说级
		}
		if (message.type === "commission") {
			const amount = Number(message.extra?.amount || 0n);
			return amount >= 50000; // 5万以上
		}
		return false;
	};

	// 高亮消息内容
	const highlightContent = (text: string) => {
		// 识别数字+DD, 档次(史诗/传说/稀有/罕见), 奖项(特等奖/一等奖/二等奖)
		const regex = /(\d+(?:\.\d+)?(?:万|k)?\s*DD)|(传说|史诗|罕见|稀有)(符券)|(特等奖|一等奖|二等奖|奥丁圣印|世界树果|瓦尔基里羽)/g;
		
		const parts = text.split(regex);
		
		return parts.map((part, i) => {
			if (!part) return null;
			
			// 检查是否匹配高亮模式
			const isAmount = /\d+(?:\.\d+)?(?:万|k)?\s*DD/.test(part);
			const isRarity = /传说|史诗|罕见|稀有/.test(part);
			const isTicket = part === "符券";
			const isPrize = /特等奖|一等奖|二等奖|奥丁圣印|世界树果|瓦尔基里羽/.test(part);
			
			if (isAmount || isPrize) {
				return <span key={i} className="font-bold text-orange-600 dark:text-orange-400">{part}</span>;
			}
			if (isRarity) {
				return <span key={i} className="font-bold text-red-600 dark:text-red-400">{part}</span>;
			}
			
			return <span key={i}>{part}</span>;
		});
	};

	const handleMessageClick = () => {
		router.push("/plaza/lucky-wheel");
	};

	if (isLoading) {
		return (
			<div className="h-full flex items-center justify-center">
				<div className="text-center text-gray-400 dark:text-gray-500">
					<div className="inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin mb-2" />
					<p className="text-sm">{t.loading}</p>
				</div>
			</div>
		);
	}

	if (messages.length === 0) {
		return (
			<div className="h-full flex flex-col items-center justify-center">
				<div className="text-center">
					<div className="text-5xl mb-3 opacity-50">🔥</div>
					<p className="text-sm font-medium text-gray-500 mb-2">
						{t.noData}
					</p>
					<Button
						variant="outline"
						size="sm"
						onClick={handleMessageClick}
						className="mt-2 h-8"
					>
						{t.goToForge}
					</Button>
				</div>
			</div>
		);
	}

	const renderItem = (msg: DynamicMessage, keyPrefix: string) => {
		const icon = getMessageIcon(msg.type);
		const isHighValue = isHighValueMessage(msg);

		return (
			<div
				key={`${keyPrefix}-${msg.id}`}
				onClick={handleMessageClick}
				className="p-2.5 rounded-xl cursor-pointer transition-all duration-300 bg-white/40 dark:bg-white/5 border border-transparent hover:border-orange-500/40 dark:hover:border-orange-500/30 hover:bg-white/80 dark:hover:bg-white/10 group"
			>
				<div className="flex items-start gap-2">
					<div className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-sm grayscale-[30%] group-hover:grayscale-0 transition-all opacity-80 group-hover:opacity-100">
						{icon}
					</div>
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-1.5 mb-0.5">
							<span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
								[{formatTime(msg.timestamp)}]
							</span>
							<span className={`text-xs font-bold truncate ${isHighValue ? "text-orange-600 dark:text-orange-400" : "text-light-text dark:text-dark-text"}`}>
								<EnhancedUserAccount
									userid={msg.user}
									showCopy={false}
									language={language}
									enableUserInfo={true}
									fallbackFormat="short"
								/>
							</span>
						</div>
						<p className={`text-[11px] leading-tight ${isHighValue ? "text-orange-700/80 dark:text-orange-300/80 font-medium" : "text-gray-500 dark:text-gray-400"}`}>
							{highlightContent(msg.message)}
						</p>
					</div>
					{isHighValue && (
						<div className="flex-shrink-0 mt-1">
							<div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
						</div>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="h-full flex flex-col">
			<div className="flex-1 relative overflow-hidden">
				<div
					className={`absolute inset-0 space-y-2 ${isPaused ? "" : "animate-forge-scroll-up"}`}
					onMouseEnter={() => setIsPaused(true)}
					onMouseLeave={() => setIsPaused(false)}
				>
					{messages.map((msg) => renderItem(msg, "first"))}
					{messages.map((msg) => renderItem(msg, "second"))}
				</div>
			</div>

			<style jsx>{`
        @keyframes forgeScrollUp {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .animate-forge-scroll-up {
          animation: forgeScrollUp 30s linear infinite;
        }
      `}</style>
		</div>
	);
}
